import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { SubscriptionBlockReason, SubscriptionTariff, TenantSubscription } from '../../types/subscriptions';
import { ChangeSubscription } from '../../workspaces/tenantConfig/changeSubscription/changeSubscription.component';
import { PaymentCardForm } from '../paymentCardForm/paymentCardForm.component';
import { BankTransferDetailsPanel } from '../bankTransferDetails/bankTransferDetails.component';
import { useSubscriptionTariffsRequests } from '../../api/subscription_tariffs';
import { useChangeTenantSubscription, useSetPaymentMethod } from '../../api/subscriptions';
import { useAuthContext } from '../../contexts/auth';
import { ERROR_SNACKBAR } from '../../consts';
import { formatNumber } from '../../utils';
import styles from './subscriptionGate.module.css';

interface SubscriptionGateProps {
  reason: SubscriptionBlockReason;
  subscription: TenantSubscription;
}

const sameId = (a: string | number | undefined, b: string | number | undefined) =>
  a !== undefined && b !== undefined && String(a) === String(b);

/**
 * Switch a lapsed subscription to bank transfer. The backend raises (or reopens)
 * this month's invoice to transfer; the gate then shows its details.
 */
const PayByTransferButton: React.FC<{ beforeSwitch?: () => Promise<boolean> }> = ({ beforeSwitch }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, setPaymentMethod } = useSetPaymentMethod();

  const handleClick = async () => {
    if (beforeSwitch && !(await beforeSwitch())) return;
    const { success } = await setPaymentMethod('BANK_TRANSFER');
    if (!success) {
      enqueueSnackbar(t('Could not change the payment method. Please try again.'), ERROR_SNACKBAR);
      return;
    }
    window.location.reload();
  };

  return (
    <Button variant="text" onClick={handleClick} disabled={loading}>
      {t('Pay by bank transfer instead')}
    </Button>
  );
};

/**
 * One form: choose a plan (defaults to the current one) and enter a card, with a
 * single submit. Used when the workspace is gated for a missing card.
 */
const CardRecoveryForm: React.FC<{ subscription: TenantSubscription }> = ({ subscription }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { query } = useSubscriptionTariffsRequests();
  const { change } = useChangeTenantSubscription();

  const [tariffs, setTariffs] = useState<SubscriptionTariff[]>([]);
  const currentTariffId = String(subscription.tariff?.id ?? '');
  const [selectedTariffId, setSelectedTariffId] = useState(currentTariffId);

  useEffect(() => {
    query().then((response) => {
      if (response?.results) {
        setTariffs(response.results.filter((tariff: SubscriptionTariff) => !tariff.is_custom_priced));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const planChanged = !sameId(selectedTariffId, currentTariffId);

  // Apply the plan change (if any) before the card is captured, so the invoice
  // the stored card settles is raised on the chosen plan.
  const applyPlanChange = useCallback(async (): Promise<boolean> => {
    if (!planChanged) return true;
    const { success } = await change({ tariff: selectedTariffId });
    if (!success) {
      enqueueSnackbar(t('Change subscription failed'), ERROR_SNACKBAR);
      return false;
    }
    return true;
  }, [change, enqueueSnackbar, planChanged, selectedTariffId, t]);

  const header = (
    <Box sx={{ textAlign: 'left', mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {t('Plan')}
      </Typography>
      <RadioGroup
        value={selectedTariffId}
        onChange={(event) => setSelectedTariffId(event.target.value)}
      >
        {tariffs.map((tariff) => (
          <FormControlLabel
            key={tariff.id}
            value={String(tariff.id)}
            control={<Radio size="small" />}
            sx={{ alignItems: 'flex-start' }}
            label={
              <Box sx={{ display: 'flex', flexDirection: 'column', pt: 0.5 }}>
                <Typography variant="body2">
                  {tariff.name}
                  {sameId(tariff.id, currentTariffId) && ` — ${t('current plan')}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('Net price')}: {formatNumber(tariff.price_net)} {t('/ month')}
                </Typography>
              </Box>
            }
          />
        ))}
      </RadioGroup>
    </Box>
  );

  return (
    <Box className={styles.picker}>
      <Typography variant="body2" className={styles.subtitle}>
        {t('Choose a plan and add a payment card to restore access. Future fees are charged to the card automatically.')}
      </Typography>
      <PaymentCardForm
        payu={subscription.payu}
        header={header}
        beforeSubmit={applyPlanChange}
        submitDisabled={tariffs.length === 0}
        submitLabel={t('Confirm and restore access')}
        onSuccess={() => window.location.reload()}
      />
      {subscription.bank_transfer?.is_enabled && (
        <PayByTransferButton beforeSwitch={applyPlanChange} />
      )}
    </Box>
  );
};

/**
 * Locked out while paying by bank transfer: show what to transfer. Access comes
 * back once staff confirm the money arrived.
 */
const TransferRecovery: React.FC<{ subscription: TenantSubscription }> = ({ subscription }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, setPaymentMethod } = useSetPaymentMethod();
  const [showCardForm, setShowCardForm] = useState(false);

  if (showCardForm) {
    return <CardRecoveryForm subscription={subscription} />;
  }

  const payByCard = async () => {
    // Without a card on file, the card form stores one and switches over.
    if (!subscription.card) {
      setShowCardForm(true);
      return;
    }
    const { success } = await setPaymentMethod('CARD');
    if (!success) {
      enqueueSnackbar(t('Could not change the payment method. Please try again.'), ERROR_SNACKBAR);
      return;
    }
    window.location.reload();
  };

  return (
    <Box className={styles.picker}>
      <Typography variant="body2" className={styles.subtitle}>
        {t('Transfer the amount below to restore access. We unlock the workspace as soon as we confirm the payment has arrived, usually within one business day.')}
      </Typography>
      <BankTransferDetailsPanel
        details={subscription.bank_transfer}
        transfers={subscription.pending_transfers}
      />
      {subscription.payu.enabled && (
        <Button variant="text" onClick={payByCard} disabled={loading}>
          {t('Pay by card instead')}
        </Button>
      )}
    </Box>
  );
};

export const SubscriptionGate: React.FC<SubscriptionGateProps> = ({ reason, subscription }) => {
  const { t } = useTranslation();
  const { logout } = useAuthContext();

  const headline: Record<SubscriptionBlockReason, string> = {
    OVERAGE: t('Your device count ({{count}}) exceeds the {{limit}} limit for the {{plan}} plan.', {
      count: subscription.measured_device_count ?? 0,
      limit: subscription.equipment_limit ?? '',
      plan: subscription.tariff.name,
    }),
    TRIAL_EXPIRED: t('Trial ended'),
    DUNNING: t('Your subscription has expired'),
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.card}>
        <LockOutlinedIcon className={styles.icon} />
        <Typography variant="h5" className={styles.title}>
          {t('Your access is blocked')}
        </Typography>
        <Typography variant="body1" className={styles.subtitle}>
          {headline[reason]}
        </Typography>

        {subscription.payment_method === 'BANK_TRANSFER' && reason !== 'OVERAGE' ? (
          <TransferRecovery subscription={subscription} />
        ) : subscription.needs_card ? (
          <CardRecoveryForm subscription={subscription} />
        ) : (
          <Box className={styles.picker}>
            <ChangeSubscription variant="gate" onSuccess={() => window.location.reload()} />
            {reason !== 'OVERAGE' && subscription.bank_transfer?.is_enabled && <PayByTransferButton />}
          </Box>
        )}

        <Button variant="text" size="small" onClick={logout}>
          {t('Log out')}
        </Button>
      </Box>
    </Box>
  );
};
